import {
    AlertLiveObject,
    ComparisonSettingsObject,
    EventLiveBookmakerObject,
    EventMarketCap,
    EventsStoredObjects,
    FixtureObject,
    HiddenObject,
    LiveOddsObject,
    LiveProcessingEvent,
    LiveSettingsObject,
    SportsFilter
} from "../../@types/response";
import {
    AVERAGE,
    BET365,
    BETFAIR,
    COMPARISONS,
    COMPARISONS_PRIORITY,
    LIVE_MARKETS,
    MARGIN_LIMIT,
    MarginMethod,
    PINNACLE
} from "../../constants/CommonConstants";
import {OrderConstants} from "../../constants/LiveConstants";
import {getBetfairChart} from "./events";

const DEFAULT_SBV = '#'

function splitLiveMarketKey(marketKey: string): { marketId: string, sbv: string } {
    if (!marketKey.includes('@')) return {marketId: marketKey, sbv: DEFAULT_SBV}
    const [marketId, ...sbvParts] = marketKey.split('@')
    return {marketId, sbv: sbvParts.join('@') || DEFAULT_SBV}
}

function getLiveMarketKey(marketId: string, sbv: string): string {
    return sbv !== DEFAULT_SBV ? `${marketId}@${sbv}` : marketId
}

function getLiveMarketSpreads(market: any): { [sbv: string]: { [signId: string]: any } } {
    if (!market) return {}
    if (market.sign) return {[DEFAULT_SBV]: market.sign}

    return Object.entries(market)
        .filter(([, value]: [string, any]) => value?.sign)
        .reduce((obj, [sbv, value]: [string, any]) => ({...obj, [sbv]: value.sign}), {})
}

function getLiveBookmakerMarketIds(markets: any): string[] {
    return [...new Set(Object.keys(markets || {}).map(marketKey => splitLiveMarketKey(marketKey).marketId))]
}

function getLiveBookmakerMarketSpreads(markets: any, marketId: string): { [sbv: string]: { [signId: string]: any } } {
    const result = {...getLiveMarketSpreads(markets?.[marketId])}
    Object.entries(markets || {}).forEach(([marketKey, market]: [string, any]) => {
        const parsed = splitLiveMarketKey(marketKey)
        if (parsed.marketId === marketId && parsed.sbv !== DEFAULT_SBV && market?.sign) {
            result[parsed.sbv] = market.sign
        }
    })
    return result
}

function getLiveMarketSigns(markets: any, marketId: string, sbv: string): { [signId: string]: any } | undefined {
    return getLiveBookmakerMarketSpreads(markets, marketId)[sbv]
}

function getLiveAlertKey(eventId: number, marketId: string, sbv: string, signId: string): string {
    return sbv !== DEFAULT_SBV ? `${eventId}-${marketId}@${sbv}-${signId}` : `${eventId}-${marketId}-${signId}`
}

/**
 * Sort Events
 *
 * @param a
 * @param b
 * @param events
 * @param settings
 */
export const sortEvent = (a: AlertLiveObject, b: AlertLiveObject, events: EventsStoredObjects, settings: LiveSettingsObject): number => {
    let result: number;
    switch (settings.order) {
        case OrderConstants.DATE:
            if (events[a.id].date !== events[b.id].date) {
                result = events[a.id].date - events[b.id].date
            } else {
                result = a.margin - b.margin
            }
            break;
        case OrderConstants.MARGIN:
            if (a.margin !== b.margin) {
                result = a.margin - b.margin
            } else {
                result = events[a.id].name > events[b.id].name ? 1 : -1
            }
            break;
        case OrderConstants.COMPARISON:
            if (a.comparisonType === b.comparisonType) {
                result = a.margin - b.margin
            } else {
                result = COMPARISONS_PRIORITY[a.comparisonType.toString()] - COMPARISONS_PRIORITY[b.comparisonType.toString()]
            }
            break;
        case OrderConstants.BOOK:
            if (a.bookmakerId === b.bookmakerId) {
                result = a.margin - b.margin
            } else {
                result = a.bookmakerId > b.bookmakerId ? 1 : -1;
            }
            break;
        case OrderConstants.NONE:
        default:
            result = 0
            break;
    }
    return result !== 0 ? result : a.marketId !== b.marketId ? a.marketId.localeCompare(b.marketId) : a.signId !== b.signId ? a.signId.localeCompare(b.signId) : 0
}

/**
 * Generate Betfair URL
 *
 * @param alert
 * @param refs
 *
 * @return string | null
 */
export const getBetfairChartUrl = (alert: AlertLiveObject, refs: EventMarketCap | undefined): string | null => {
    if (!refs) return null
    try {
        const marketRef = refs[getLiveMarketKey(alert.marketId, alert.sbv || DEFAULT_SBV)] ?? refs[alert.marketId]
        const bMarketId = marketRef?.['bMarketId']?.toString()
        const bSelectionId = marketRef?.[alert.signId.toString()]?.toString()
        if (!bMarketId || !bSelectionId) return null
        return getBetfairChart(bMarketId!, bSelectionId!)
    } catch (e) {
        return null
    }
}

/**
 * Apply Filters
 *
 * @param filter
 * @param sports
 * @param settings
 */
export const calculateFilter = (filter: SportsFilter, sports: FixtureObject, settings: SportsFilter): SportsFilter => {
    const result: SportsFilter = {...settings}
    Object.keys(filter).forEach(sportId => {
        if (result[sportId] === true) {
            result[sportId] = Object.fromEntries(Object.keys(sports[sportId]).map(k => [k, true]))
        }
        if (filter[sportId] === true || Object.keys(filter[sportId]).length === 0) {
            result[sportId] = filter[sportId]
            return
        } else if (!(sportId in result)) {
            result[sportId] = {}
        }
        Object.keys(filter[sportId]).forEach(categoryId => {
            // @ts-ignore
            result[sportId][categoryId] = filter[sportId][categoryId]
        })
    })
    return result
}


/**
 * eventsListener
 *
 * @returns AlertLiveObject
 * @param event
 * @param settings
 * @param booked
 * @param hides
 */
export function processEvent(event: LiveProcessingEvent, settings: LiveSettingsObject, booked: string[], hides: HiddenObject): AlertLiveObject[] {
    let alerts: any = [];
    try {

        if (!('bookmakers' in event) || !settings) {
            return []
        }

        const bookmakers: any = event.bookmakers
        const bookmakerToCompare: EventLiveBookmakerObject[] = Object.keys(event.bookmakers || {})
            .filter((bookmakerId) => settings.bookmakers.includes(parseInt(bookmakerId)) && !COMPARISONS.includes(parseInt(bookmakerId)))
            .map(bookmakerId => Object.assign({}, bookmakers[bookmakerId], {id: bookmakerId}))

        //Start BETFAIR calculation
        if (settings.comparisons[BETFAIR.toString()]?.status && Object.keys(bookmakers).includes(BETFAIR.toString())) {
            const bComparison = Object.assign({}, bookmakers[BETFAIR.toString()], {comparisonType: BETFAIR})
            alerts = alerts.concat(
                processLiveComparison(event._id, bComparison, bookmakerToCompare, settings, booked, hides)
            )
        }

        //Start PINNACLE calculation
        if (settings.comparisons[PINNACLE.toString()]?.status && Object.keys(bookmakers).includes(PINNACLE.toString())) {
            const bComparison = Object.assign({}, bookmakers[PINNACLE.toString()], {comparisonType: PINNACLE})
            alerts = alerts.concat(
                processLiveComparison(event._id, bComparison, bookmakerToCompare, settings, booked, hides)
            )
        }

        //Start BET365 calculation
        if (settings.comparisons[BET365.toString()]?.status && Object.keys(bookmakers).includes(BET365.toString())) {
            const bComparison = Object.assign({}, bookmakers[BET365.toString()], {comparisonType: BET365})
            const compareList = bookmakerToCompare.filter(b => parseInt(b.id) !== BET365)
            alerts = alerts.concat(
                processLiveComparison(event._id, bComparison, compareList, settings, booked, hides)
            )
        }

        //Start AVERAGE calculation
        const averageBookmakers = settings.comparisons[AVERAGE.toString()]?.bookmakers
        const activeBookmakers: EventLiveBookmakerObject[] = Object.keys(event.bookmakers || {})
            .filter((bookmakerId) => !COMPARISONS.includes(parseInt(bookmakerId)))
            .filter((bookmakerId) => averageBookmakers?.length ? averageBookmakers.includes(parseInt(bookmakerId)) : false)
            .map((bookmakerId) => event.bookmakers[bookmakerId])
        if (settings.comparisons[AVERAGE.toString()]?.status && activeBookmakers.length > 0) {
            const bComparison = getLiveAverageBookmaker(event._id, activeBookmakers)
            alerts = alerts.concat(
                processLiveComparison(event._id, bComparison, bookmakerToCompare, settings, booked, hides)
            )
        }
    } catch (error) {
        console.error('Error on process event', error, event)
    }
    return alerts
}


/**
 * get Average Bookmakers
 *
 * @returns {}
 * @param eventId
 * @param bookmakers
 */
export function getLiveAverageBookmaker(eventId: number, bookmakers: EventLiveBookmakerObject[]) {
    const bookmakersMarkets: Set<string> = new Set(bookmakers.flatMap(b => getLiveBookmakerMarketIds(b.markets)))
    let markets: any = {}
    bookmakersMarkets.forEach((marketId) => {
        const sbvs: Set<string> = new Set(bookmakers.flatMap(b => Object.keys(getLiveBookmakerMarketSpreads(b.markets, marketId))))

        sbvs.forEach(sbv => {
            let signs: string[] = Object.keys(LIVE_MARKETS[marketId]?.outcomes ?? [])
            let count: { [signId: string]: number } = signs.reduce((obj, item) => {
                return {...obj, [item]: 0};
            }, {}), sum: { [signId: string]: number } = Object.assign({}, count)

            let signAverage: any = {}
            for (const bookmaker of bookmakers) {
                const bookmakerSigns = getLiveMarketSigns(bookmaker.markets, marketId, sbv)
                if (!bookmakerSigns) continue
                if (signs.length === 0) {
                    signs = Object.keys(bookmakerSigns)
                    count = signs.reduce((obj, item) => ({...obj, [item]: 0}), {})
                    sum = Object.assign({}, count)
                }
                for (const signId of signs) {
                    let backOdd = bookmakerSigns[signId]?.backOdd
                    if (backOdd > 1) {
                        sum[signId] += backOdd
                        count[signId]++
                    }
                }
            }

            for (const signId of signs) {
                signAverage[signId] = {
                    outcomePosition: signId,
                    backOdd: count[signId] ? Math.round((sum[signId] / count[signId]) * 100) / 100 : 1,
                    firstBackOdd: 100,
                    updt: new Date().getTime()
                }
            }

            const marketKey = getLiveMarketKey(marketId, sbv)
            if (!markets[marketKey]) markets[marketKey] = {}
            if (sbv === DEFAULT_SBV) {
                markets[marketId].sign = signAverage
            } else {
                markets[marketKey].sign = signAverage
            }
        })
    })

    return {
        id: eventId.toString(),
        eventId: eventId,
        playability: 1,
        comparisonType: AVERAGE,
        markets
    }
}

/**
 * Process Comparison
 *
 * @param eventId
 * @param comparison
 * @param bookmakers
 * @param settings
 * @param booked
 * @param hides
 */
export function processLiveComparison(eventId: number, comparison: EventLiveBookmakerObject, bookmakers: EventLiveBookmakerObject[], settings: LiveSettingsObject, booked: string[], hides: HiddenObject
): AlertLiveObject[] {
    // @ts-ignore
    return getLiveBookmakerMarketIds(comparison?.markets || {})
        .map((marketId: string) =>
            Object.entries(getLiveBookmakerMarketSpreads(comparison.markets, marketId))
                .flatMap(([sbv, signs]) => Object.entries(signs)
                    .map(([signId, sign]) => ({...sign, signId, sbv}))
                    .filter(sign => !booked.includes(getLiveAlertKey(eventId, marketId, sign.sbv, sign.signId)))
                    .map(sign => processLiveBookmakers(eventId, comparison, bookmakers, marketId.toString(), sign.sbv.toString(), sign.signId.toString(), settings))
                    .filter((alert): alert is AlertLiveObject => alert != null)
                    .filter(alert => {
                        const alertKey = getLiveAlertKey(eventId, alert.marketId, alert.sbv || DEFAULT_SBV, alert.signId)
                        return !Object.keys(hides).includes(alertKey) || hides[alertKey] > alert.margin
                    })
                ))
        .filter(x => x.length)
        .flat();
}


/**
 * Process Bookmakers
 *
 * @param eventId
 * @param comparison
 * @param bookmakers
 * @param marketId
 * @param sbv
 * @param signId
 * @param settings
 */
function processLiveBookmakers(eventId: number, comparison: EventLiveBookmakerObject, bookmakers: EventLiveBookmakerObject[], marketId: string, sbv: string, signId: string, settings: LiveSettingsObject): null | AlertLiveObject {
    return bookmakers
        .map((bookmaker: EventLiveBookmakerObject) => {
            //Has Market and SBV
            if (!bookmaker.markets || !getLiveMarketSigns(bookmaker.markets, marketId, sbv)) return null
            // Get comparison odd
            const comparisonSigns = getLiveMarketSigns(comparison.markets, marketId, sbv)
            const bookmakerSigns = getLiveMarketSigns(bookmaker.markets, marketId, sbv)
            const compOdd = comparisonSigns?.[signId]?.backOdd
            if (compOdd <= 1) return null
            //Get bookamker odd
            const bookOdd = bookmakerSigns?.[signId]?.backOdd
            if ((bookOdd && bookOdd < settings.from) || (settings.to !== 1 && bookOdd > settings.to)) return null
            //Apply bound
            const comparisonSettings = settings.comparisons[comparison.comparisonType.toString()]
            const bookins = getLiveBookmakerBooking(comparison, marketId, sbv)
            const margin = calculateMargin(
                compOdd,
                bookOdd,
                comparisonSettings.margin,
                comparisonSettings.method,
                bookins,
                Object.keys(comparisonSigns || {}).length
            )
            if (margin > comparisonSettings.threshold) return null
            return {
                id: eventId,
                comparisonType: comparison.comparisonType,
                marketId: marketId,
                sbv: sbv,
                signId: signId,
                bookmakerId: parseInt(bookmaker.id),
                playability: 1,
                margin: margin,
                odds: getOdds(bookmaker, marketId, sbv),
                comparison: getOdds(comparison, marketId, sbv)
            }
        })
        .filter((alert: any) => alert?.margin && alert.margin < MARGIN_LIMIT)
        .sort((a: any, b: any) => a.margin - b.margin)
        .find((_, idx) => idx === 0) || null
}

function getLiveBookmakerBooking(bookmaker: any, marketId: string, sbv: string) {
    const signs = getLiveMarketSigns(bookmaker.markets, marketId, sbv) || {}
    let bookings = 0
    for (const signId of Object.keys(signs)) {
        bookings += 100 / signs[signId].backOdd
    }
    return bookings
}

/**
 * newLiveSetting
 *
 * @param settings
 */
export function buildSettings(settings: LiveSettingsObject): LiveSettingsObject {
    Object.keys(settings.comparisons)
        .forEach((comparisonId) => {
            settings.comparisons[comparisonId] = buildComparisonSettings(settings.comparisons[comparisonId], comparisonId)
        })
    return settings
}

/**
 * newLiveSetting
 *
 * @param settings
 * @param comparisonId
 */
function buildComparisonSettings(settings: ComparisonSettingsObject, comparisonId: string): ComparisonSettingsObject {
    settings.margin = comparisonId === BETFAIR.toString() ? null : (settings.margin ?? 100 - (settings.bound || 0))
    settings.threshold = settings.threshold ?? (settings.limit || 0)
    settings.amountMarket = comparisonId === AVERAGE.toString() ? 0 : (settings.amountMarket ?? 1000)
    settings.amountOutcome = comparisonId === PINNACLE.toString() || comparisonId === AVERAGE.toString() ? 0 : (settings.amountOutcome ?? 100)
    settings.method = settings.method ?? MarginMethod.ORIGINAL
    settings.bookmakers = comparisonId === AVERAGE.toString() && settings.bookmakers ? settings.bookmakers : []
    settings.booking = 0
    return settings
}

function calculateMargin(comparisonOdd: number, bookmakerOdd: number, margin: null | number, method: null | MarginMethod, bookings: number, signs: number): number {
    const boundComparisonOdd = (margin === null) ? comparisonOdd : 100 / ((100 / comparisonOdd) - ((bookings - margin) / signs))
    let result = (method === MarginMethod.NEW) ? ((100 / bookmakerOdd) - (100 / boundComparisonOdd)) : (boundComparisonOdd - bookmakerOdd) / (boundComparisonOdd / 100)
    return Math.round(result * 10) / 10
}

function getOdds(bookmaker: any, marketId: string, sbv: string): LiveOddsObject[] {
    const marketOdds = getLiveMarketSigns(Object.assign({}, bookmaker.markets), marketId, sbv) || {}

    return Object
        .keys(marketOdds)
        .map((signId) => {
            return {
                signId: signId,
                sign: LIVE_MARKETS[marketId]?.outcomes?.[signId]?.name ?? signId,
                odd: Math.round(marketOdds[signId]?.backOdd * 100) / 100 || 0,
                firstBackOdd: marketOdds[signId]?.firstBackOdd || 0,
                sbv: sbv
            }
        })
}
